import { analytics } from '@/shared/config/firebase';
import { logEvent, setUserId, setUserProperties, Analytics } from 'firebase/analytics';

/**
 * Détecte les informations sur l'appareil et la plateforme
 */
const getDeviceInfo = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  const userAgent = navigator.userAgent;
  const platform = navigator.platform;
  
  // Détecter le type d'appareil
  const isMobile = /iPhone|iPad|iPod|Android/i.test(userAgent);
  const isTablet = /iPad|Android/i.test(userAgent) && !/Mobile/i.test(userAgent);
  const isDesktop = !isMobile && !isTablet;
  
  // Détecter le navigateur
  let browser = 'unknown';
  if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) browser = 'Chrome';
  else if (userAgent.includes('Firefox')) browser = 'Firefox';
  else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) browser = 'Safari';
  else if (userAgent.includes('Edg')) browser = 'Edge';
  else if (userAgent.includes('Opera') || userAgent.includes('OPR')) browser = 'Opera';
  
  // Détecter le système d'exploitation
  let os = 'unknown';
  if (userAgent.includes('Windows')) os = 'Windows';
  else if (userAgent.includes('Mac OS X') || userAgent.includes('Macintosh')) os = 'macOS';
  else if (userAgent.includes('Linux')) os = 'Linux';
  else if (userAgent.includes('Android')) os = 'Android';
  else if (userAgent.includes('iOS') || /iPhone|iPad|iPod/.test(userAgent)) os = 'iOS';
  
  // Détecter si c'est une app mobile (PWA installée)
  const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                (window.navigator as any).standalone === true;
  
  return {
    device_type: isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop',
    browser,
    os,
    platform,
    is_pwa: isPWA,
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
  };
};

/**
 * Service pour gérer Firebase Analytics
 */
class AnalyticsService {
  private getAnalytics(): Analytics | null {
    return analytics;
  }

  /**
   * Initialise les propriétés utilisateur avec les informations de l'appareil
   * À appeler au démarrage de l'application
   */
  initializeUserProperties(): void {
    const deviceInfo = getDeviceInfo();
    this.setUserProperties(deviceInfo);
  }

  /**
   * Enregistre un événement personnalisé
   */
  logEvent(eventName: string, eventParams?: Record<string, any>): void {
    const analyticsInstance = this.getAnalytics();
    if (!analyticsInstance) {
      console.warn('Analytics non disponible');
      return;
    }

    try {
      logEvent(analyticsInstance, eventName, eventParams);
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement de l\'événement Analytics:', error);
    }
  }

  /**
   * Définit l'ID utilisateur pour le tracking
   */
  setUserId(userId: string | null): void {
    const analyticsInstance = this.getAnalytics();
    if (!analyticsInstance) {
      return;
    }

    try {
      if (userId) {
        setUserId(analyticsInstance, userId);
      }
    } catch (error) {
      console.error('Erreur lors de la définition de l\'ID utilisateur:', error);
    }
  }

  /**
   * Définit des propriétés utilisateur personnalisées
   */
  setUserProperties(properties: Record<string, any>): void {
    const analyticsInstance = this.getAnalytics();
    if (!analyticsInstance) {
      return;
    }

    try {
      setUserProperties(analyticsInstance, properties);
    } catch (error) {
      console.error('Erreur lors de la définition des propriétés utilisateur:', error);
    }
  }

  /**
   * Enregistre une vue de page avec informations sur l'appareil
   */
  logPageView(pageName: string, pagePath?: string): void {
    const deviceInfo = getDeviceInfo();
    this.logEvent('page_view', {
      page_title: pageName,
      page_path: pagePath || (typeof window !== 'undefined' ? window.location.pathname : ''),
      page_location: typeof window !== 'undefined' ? window.location.href : '',
      ...deviceInfo,
    });
  }

  /**
   * Enregistre un événement de conversion
   */
  logConversion(conversionName: string, value?: number, currency?: string): void {
    this.logEvent('conversion', {
      conversion_name: conversionName,
      value,
      currency: currency || 'EUR',
    });
  }

  /**
   * Enregistre un événement de commande
   */
  logPurchase(orderId: string, value: number, currency: string = 'EUR', items?: any[]): void {
    this.logEvent('purchase', {
      transaction_id: orderId,
      value,
      currency,
      items,
    });
  }

  /**
   * Enregistre un événement d'ajout au panier
   */
  logAddToCart(value: number, currency: string = 'EUR', items?: any[]): void {
    this.logEvent('add_to_cart', {
      value,
      currency,
      items,
    });
  }

  /**
   * Enregistre un événement de début de checkout
   */
  logBeginCheckout(value: number, currency: string = 'EUR'): void {
    this.logEvent('begin_checkout', {
      value,
      currency,
    });
  }
}

export const analyticsService = new AnalyticsService();
